import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  BulkActionRequest,
  MoveApplicationRequest,
  MoveApplicationResponse,
  PipelineActionView,
  PipelineBoardResponse,
  PipelineExportInput,
  PipelineFilter,
  PipelineListResponse,
  PipelineOptions,
  SaveViewRequest,
  SavedViewRow,
  TransferApplicationRequest,
  TransferApplicationResponse,
  UpdateViewInput,
} from '@nest-recruit/shared';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from '../../../lib/api-client';

// S16: SCR-02 and SCR-03. The keys sit under `['applications']`, so every hint the API
// already sends when an application changes — a sorting decision, a restore, a move on
// someone else's board — refetches the open list and board without a word from here.

export function pipelineKey(jobId: string): readonly unknown[] {
  return ['applications', 'pipeline', jobId];
}

const OPTIONS_KEY = ['pipeline', 'options'] as const;
const VIEWS_KEY = ['pipeline', 'views'] as const;
const OPTIONS_STALE_MS = 60_000;
const PROGRESS_POLL_MS = 1000;

export function actionKey(actionId: string): readonly unknown[] {
  return ['pipeline-action', actionId];
}

/** SCR-02. The previous page stays on screen while the next loads (§5a rule 2). */
export function usePipelineList(jobId: string, filter: PipelineFilter, page: number, isEnabled: boolean): UseQueryResult<PipelineListResponse, ApiError> {
  return useQuery<PipelineListResponse, ApiError>({
    queryKey: [...pipelineKey(jobId), 'list', filter, page],
    enabled: isEnabled,
    queryFn: () => apiGet<PipelineListResponse>(`/jobs/${jobId}/pipeline`, { query: { ...filter, page } }),
    placeholderData: (previous) => previous,
  });
}

/** SCR-03. */
export function usePipelineBoard(jobId: string, filter: PipelineFilter, isEnabled: boolean): UseQueryResult<PipelineBoardResponse, ApiError> {
  return useQuery<PipelineBoardResponse, ApiError>({
    queryKey: [...pipelineKey(jobId), 'board', filter],
    enabled: isEnabled,
    queryFn: () => apiGet<PipelineBoardResponse>(`/jobs/${jobId}/pipeline/board`, { query: { ...filter } }),
    placeholderData: (previous) => previous,
  });
}

/** The pickers' lists: reasons with their stage, pools, tags. */
export function usePipelineOptions(isEnabled = true): UseQueryResult<PipelineOptions, ApiError> {
  return useQuery<PipelineOptions, ApiError>({
    queryKey: OPTIONS_KEY,
    enabled: isEnabled,
    queryFn: () => apiGet<PipelineOptions>('/pipeline/options'),
    staleTime: OPTIONS_STALE_MS,
  });
}

// --- Moves, bulk actions, undo -------------------------------------------------------------

export function moveApplication(applicationId: string, body: MoveApplicationRequest, via: 'board' | 'list'): Promise<MoveApplicationResponse> {
  return apiPost<MoveApplicationResponse>(`/applications/${applicationId}/move${via === 'list' ? '?via=list' : ''}`, body);
}

export function startBulkAction(jobId: string, body: BulkActionRequest): Promise<PipelineActionView> {
  return apiPost<PipelineActionView>(`/jobs/${jobId}/pipeline/actions`, body);
}

export function undoAction(actionId: string): Promise<PipelineActionView> {
  return apiPost<PipelineActionView>(`/pipeline/actions/${actionId}/undo`);
}

export function transferApplication(applicationId: string, body: TransferApplicationRequest): Promise<TransferApplicationResponse> {
  return apiPost<TransferApplicationResponse>(`/applications/${applicationId}/transfer`, body);
}

/**
 * A background bulk action's progress (§L9 "with a progress indicator"). Polled while it
 * runs — this is a progress bar, which is the one place a poll is the point — and nudged
 * by the API's hint on the same key.
 */
export function usePipelineAction(actionId: string | null): UseQueryResult<PipelineActionView, ApiError> {
  return useQuery<PipelineActionView, ApiError>({
    queryKey: actionKey(actionId ?? 'none'),
    queryFn: () => apiGet<PipelineActionView>(`/pipeline/actions/${actionId ?? ''}`),
    enabled: actionId !== null,
    refetchInterval: (query) => (isRunning(query.state.data) ? PROGRESS_POLL_MS : false),
  });
}

export function isRunning(view: PipelineActionView | undefined): boolean {
  return view?.status === 'queued' || view?.status === 'running';
}

/**
 * §L9 export: the rows selected and the columns shown, as a file. A POST, so the browser
 * cannot download it by link; it arrives as a blob and is saved under the job's name.
 */
export async function downloadExport(jobId: string, body: PipelineExportInput, fileName: string): Promise<void> {
  const response = await fetch(`/api/v1/jobs/${jobId}/pipeline/export`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  });
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    throw new ApiError({ code: 'export_failed', message: messageOf(payload) ?? "Couldn't make the file. Try again.", status: response.status });
  }
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

function messageOf(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null || !('error' in payload)) return null;
  const error = payload.error;
  if (typeof error !== 'object' || error === null || !('message' in error)) return null;
  return typeof error.message === 'string' ? error.message : null;
}

// --- Saved views (F8.14) --------------------------------------------------------------------

export function useSavedViews(): UseQueryResult<readonly SavedViewRow[], ApiError> {
  return useQuery<readonly SavedViewRow[], ApiError>({
    queryKey: VIEWS_KEY,
    queryFn: () => apiGet<readonly SavedViewRow[]>('/pipeline/views'),
  });
}

export function useSaveView() {
  const queryClient = useQueryClient();
  return useMutation<SavedViewRow, ApiError, SaveViewRequest>({
    mutationFn: (body) => apiPost<SavedViewRow>('/pipeline/views', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: VIEWS_KEY }),
  });
}

export function useUpdateView() {
  const queryClient = useQueryClient();
  return useMutation<SavedViewRow, ApiError, { readonly id: string; readonly body: UpdateViewInput }>({
    mutationFn: ({ id, body }) => apiPatch<SavedViewRow>(`/pipeline/views/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: VIEWS_KEY }),
  });
}

export function useDeleteView() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => apiDelete<void>(`/pipeline/views/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: VIEWS_KEY }),
  });
}

/** After an action that may have named a new pool or tag, the pickers learn it. */
export function refreshOptions(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: OPTIONS_KEY });
}
